import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Paper, Typography, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableRow, TableHead,
  Toolbar, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  MenuItem, IconButton, Tooltip, Checkbox, TablePagination, InputAdornment
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { useDebounce } from "../../hooks/useDebounce";
import api from "../../api";
import SnackBarComponent from "../../components/SnackBarComponent";

export default function UvMissionsPage() {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination & Search & Selection
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const [selected, setSelected] = useState([]);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title_prefix: "",
    title: "",
    description: "",
    difficulty_level: "ROOKIE",
    duration: "",
    format: [],
    image_url: "",
    mission_category: "",
    month: "",
    reward: [],
    year: new Date().getFullYear().toString(),
    status: "ACTIVE"
  });

  const [saving, setSaving] = useState(false);
  const [deletingBulk, setDeletingBulk] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchMissions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/uv_missions");
      const fetchedData = res.data?.data?.items || res.data?.data || [];
      setMissions(Array.isArray(fetchedData) ? fetchedData : []);
    } catch (err) {
      console.error("Failed to fetch missions config:", err);
      showSnackbar("Failed to load UV Missions Config.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  // Filtering
  const filteredMissions = useMemo(() => {
    if (!debouncedSearchQuery) return missions;
    const lowerQuery = debouncedSearchQuery.toLowerCase();
    return missions.filter(m =>
      (m.title && m.title.toLowerCase().includes(lowerQuery)) ||
      (m.mission_category && m.mission_category.toLowerCase().includes(lowerQuery)) ||
      (m.difficulty_level && m.difficulty_level.toLowerCase().includes(lowerQuery))
    );
  }, [missions, debouncedSearchQuery]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchQuery]);

  const displayedMissions = filteredMissions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Selection Handlers
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = displayedMissions.map((n) => n.m_id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Pagination Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Form Handlers
  const handleOpenAdd = () => {
    setFormData({
      title_prefix: "",
      title: "",
      description: "",
      difficulty_level: "ROOKIE",
      duration: "",
      format: [""],
      image_url: "",
      mission_category: "",
      month: "",
      reward: [""],
      year: new Date().getFullYear().toString(),
      status: "ACTIVE"
    });
    setIsEditing(false);
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleOpenEdit = (mission) => {
    setFormData({
      title_prefix: mission.title_prefix || "",
      title: mission.title || "",
      description: mission.description || "",
      difficulty_level: mission.difficulty_level || "ROOKIE",
      duration: mission.duration || "",
      format: Array.isArray(mission.format) && mission.format.length > 0 ? mission.format : [""],
      image_url: mission.image_url || "",
      mission_category: mission.mission_category || "",
      month: mission.month || "",
      reward: Array.isArray(mission.reward) && mission.reward.length > 0 ? mission.reward : [""],
      year: mission.year ? mission.year.toString() : "",
      status: mission.status || "ACTIVE"
    });
    setIsEditing(true);
    setEditingId(mission.m_id);
    setOpenDialog(true);
  };

  const handleDelete = async (m_id) => {
    if (!window.confirm("Are you sure you want to delete this mission config?")) return;
    try {
      await api.delete(`/api/uv_missions/${m_id}`);
      showSnackbar("Mission config deleted successfully!", "success");
      fetchMissions();
      setSelected(selected.filter(id => id !== m_id));
    } catch (err) {
      console.error("Delete failed:", err);
      showSnackbar("Failed to delete mission config.", "error");
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selected.length} selected mission(s)?`)) return;
    setDeletingBulk(true);
    try {
      await api.post(`/api/uv_missions/bulk-delete`, { ids: selected });
      showSnackbar("Selected mission configs deleted successfully!", "success");
      setSelected([]);
      fetchMissions();
    } catch (err) {
      console.error("Bulk delete failed:", err);
      showSnackbar("Failed to delete selected missions.", "error");
    } finally {
      setDeletingBulk(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData(prev => ({ ...prev, [field]: newArray }));
  };

  const handleAddArrayItem = (field) => {
    setFormData(prev => ({ ...prev, [field]: [...prev[field], ""] }));
  };

  const handleRemoveArrayItem = (field, index) => {
    const newArray = [...formData[field]];
    newArray.splice(index, 1);
    setFormData(prev => ({ ...prev, [field]: newArray }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      showSnackbar("Title is required.", "warning");
      return;
    }

    setSaving(true);
    try {
      // Process strings in arrays and remove empty entries
      const processedFormat = formData.format.map(item => typeof item === "string" ? item.trim() : item).filter(Boolean);
      const processedReward = formData.reward.map(item => typeof item === "string" ? item.trim() : item).filter(Boolean);

      const payload = {
        ...formData,
        format: processedFormat,
        reward: processedReward,
        year: formData.year ? parseInt(formData.year, 10) : null
      };

      if (isEditing) {
        await api.put(`/api/uv_missions/${editingId}`, payload);
        showSnackbar("Mission config updated successfully!", "success");
      } else {
        await api.post("/api/uv_missions", payload);
        showSnackbar("Mission config created via lambda successfully!", "success");
      }
      setOpenDialog(false);
      fetchMissions();
    } catch (err) {
      console.error("Save failed:", err);
      showSnackbar(err.response?.data?.msg || "Failed to save mission config.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%", px: { xs: 2, sm: 3, md: 4 }, pb: 4 }}>
      {snackbar.open && (
        <SnackBarComponent
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        />
      )}

      <Paper sx={{ width: "100%", mb: 3 }}>
        <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          {selected.length > 0 ? (
            <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1" component="div">
              {selected.length} selected
            </Typography>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AssignmentIcon color="primary" />
              <Typography variant="h6" component="div">
                UV MISSION CONFIG
              </Typography>
            </Box>
          )}

          {selected.length > 0 ? (
            <Tooltip title="Delete Selected">
              <IconButton onClick={handleBulkDelete} color="error" disabled={deletingBulk}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search Missions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                sx={{ whiteSpace: "nowrap" }}
              >
                Add Mission
              </Button>
            </Box>
          )}
        </Toolbar>

        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="uv missions config table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selected.length > 0 && selected.length < displayedMissions.length}
                    checked={displayedMissions.length > 0 && selected.length === displayedMissions.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Difficulty</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size="1.5rem" />
                  </TableCell>
                </TableRow>
              ) : displayedMissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography color="textSecondary">No mission configs found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedMissions.map((m, idx) => {
                  const isItemSelected = isSelected(m.m_id);
                  const labelId = `enhanced-table-checkbox-${idx}`;

                  return (
                    <TableRow
                      key={m.m_id || idx}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onChange={(event) => handleClick(event, m.m_id)}
                          inputProps={{ "aria-labelledby": labelId }}
                        />
                      </TableCell>
                      <TableCell>{m.title}</TableCell>
                      <TableCell>{m.mission_category}</TableCell>
                      <TableCell>{m.difficulty_level}</TableCell>
                      <TableCell>{m.duration}</TableCell>
                      <TableCell>{m.status}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenEdit(m)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(m.m_id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredMissions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Add/Edit Mission Config Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? "Edit Mission" : "Add New Mission via Lambda"}</DialogTitle>
        <DialogContent dividers>
          <form id="mission-config-form" onSubmit={handleSave}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Title Prefix"
                  name="title_prefix"
                  value={formData.title_prefix}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  placeholder="e.g. SQ_M3"
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Status"
                  name="status"
                  select
                  value={formData.status}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                  <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Mission Category"
                  name="mission_category"
                  value={formData.mission_category}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  placeholder="e.g. IMPACT, PERFORMANCE"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Difficulty Level"
                  name="difficulty_level"
                  select
                  value={formData.difficulty_level}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="ROOKIE">ROOKIE</MenuItem>
                  <MenuItem value="INTERMEDIATE">INTERMEDIATE</MenuItem>
                  <MenuItem value="EXPERT">EXPERT</MenuItem>
                  <MenuItem value="LEGENDARY">LEGENDARY</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  placeholder="e.g. 7 Days"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Month"
                  name="month"
                  value={formData.month}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  placeholder="e.g. April"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Year"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  placeholder="e.g. 2026"
                />
              </Grid>

              {/* Format Fields */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Formats</Typography>
                {formData.format.map((item, index) => (
                  <Box key={`format-${index}`} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={item}
                      onChange={(e) => handleArrayChange("format", index, e.target.value)}
                      placeholder="e.g. GoPro Footage"
                    />
                    <IconButton color="error" onClick={() => handleRemoveArrayItem("format", index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => handleAddArrayItem("format")}>
                  Add Format
                </Button>
              </Grid>

              {/* Reward Fields */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Rewards</Typography>
                {formData.reward.map((item, index) => (
                  <Box key={`reward-${index}`} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={item}
                      onChange={(e) => handleArrayChange("reward", index, e.target.value)}
                      placeholder="e.g. 300 XP"
                    />
                    <IconButton color="error" onClick={() => handleRemoveArrayItem("reward", index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => handleAddArrayItem("reward")}>
                  Add Reward
                </Button>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Image URL"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  placeholder="https://cdn.uv.com/missions/apr1.jpg"
                />
              </Grid>

            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            type="submit"
            form="mission-config-form"
            variant="contained"
            color="primary"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? "Saving..." : (isEditing ? "Update Mission" : "Save Mission")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
